import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faUpload, faEdit, faToggleOn, faToggleOff,
  faSpinner, faBuilding, faSearch, faSync, faTimes,
  faFilter, faChevronDown, faTrash, faTriangleExclamation, faExternalLinkAlt,
  faCheck, faBan, faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import CSVUploadModal from '../../components/Invoicing/CSVUploadModal';
import TradeOpsDeleteModal from '../../components/TradeOpsDeleteModal';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSButton from '../../components/ui/HMSButton';
import HMSEmptyState from '../../components/ui/HMSEmptyState';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import {
  fetchCostCentersByEntity, createCostCenter, updateCostCenter, deleteCostCenter,
  uploadCostCentresCSV, fetchHMSEntities, fetchPendingIMTEntities,
  fetchWaitingCostCentres, approveCostCentre, rejectCostCentre, approveCostCentres,
} from '../../services/invoicingService';
import ListLoadError from '../../components/ListLoadError';
import { extractApiError } from '../../utils/errorMessages';
import { useFlashMessage } from '../../components/FlashMessage';
import { useAuth } from '../../context/AuthContext';
import './OutgoingInvoice.css';
import '../../components/Invoicing/InvoiceSearchFilter.css';

const TEMPLATE_COLUMNS = ['Cost Centre Number', 'Shortname', 'Description', 'Department', 'Abacus Code'];

// IMT palette (parity/DIFFERENCES/02): green for go, gold for "waiting".
const IMT_GREEN = '#4a6b25';
const IMT_GREEN_DARK = '#3a5b15';
const IMT_GOLD = '#ffc20c';
const IMT_GOLD_SOFT = '#fff6d6';

const pillStyle = (background, color) => ({
  marginLeft: 6, fontSize: '0.7em', background, color, padding: '2px 8px',
  borderRadius: 12, fontWeight: 600, fontStyle: 'normal', whiteSpace: 'nowrap',
});

const smallButton = (variant) => {
  const base = {
    fontSize: '0.85em', padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
    fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6,
    transition: 'all 0.3s ease',
  };
  if (variant === 'approve') return { ...base, background: IMT_GREEN, color: 'white', border: `1px solid ${IMT_GREEN_DARK}` };
  if (variant === 'reject') return { ...base, background: 'white', color: '#b91c1c', border: '1px solid #b91c1c' };
  return { ...base, background: 'white', color: IMT_GREEN, border: `1px solid ${IMT_GREEN}` };
};

const CostCentresPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const isFinancialApprover = user?.is_financial_approver || user?.is_superuser;
  // IMT only (divergence 07). Same rule as the server
  // (invoicing/services/delivery_cost_centre_service.can_approve): the
  // Cost Centre Approver tick-box, or a superuser.
  const canApproveCostCentres = Boolean(user?.can_approve_cost_centres || user?.is_superuser);
  const { showError, showSuccess, showInfo } = useFlashMessage();
  const [centres, setCentres] = useState([]);
  const [entities, setEntities] = useState([]);
  // A company list that failed to load must say so. Swallowed, it left no
  // company to choose and an empty table that read as "no cost centres" -- which
  // is what Finance-only staff saw while the list refused them (ledger row 124).
  const [entitiesError, setEntitiesError] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [filters, setFilters] = useState({ is_active: '', department: '' });
  const [showFilters, setShowFilters] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingCentre, setEditingCentre] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [deleteWarningMsg, setDeleteWarningMsg] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [pendingEntities, setPendingEntities] = useState([]);
  const [pendingCenters, setPendingCenters] = useState(0);
  const [pendingBannerDismissed, setPendingBannerDismissed] = useState(false);

  // IMT only (divergence 07): cost centres made for new deliveries, waiting
  // for a Cost Centre Approver.
  const [waiting, setWaiting] = useState([]);
  const [approversCount, setApproversCount] = useState(null);
  const [waitingError, setWaitingError] = useState(null);
  const [selectedWaiting, setSelectedWaiting] = useState([]);
  // Abacus codes typed on waiting rows, by cost centre id (companies that send to Abacus only).
  const [abacusCodes, setAbacusCodes] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [decidingId, setDecidingId] = useState(null);
  const [approvingSelected, setApprovingSelected] = useState(false);
  const [waitingLoaded, setWaitingLoaded] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const focusedHashRef = useRef(null);
  const refreshedForHashRef = useRef(null);

  useEffect(() => { loadEntities(); loadPendingEntities(); }, []);

  const loadWaiting = useCallback(async () => {
    setWaitingError(null);
    try {
      const data = await fetchWaitingCostCentres();
      // A fresh list every load, so a reload always counts as new (the bell
      // focus below waits on it).
      const rows = [...(data.results || [])];
      setWaiting(rows);
      setApproversCount(typeof data.approvers_count === 'number' ? data.approvers_count : null);
      // A tick on a row that is no longer waiting must not be approved by accident.
      setSelectedWaiting(prev => prev.filter(id => rows.some(r => r.id === id)));
      setWaitingLoaded(true);
    } catch (err) {
      console.error('Failed to load cost centres waiting for approval:', err);
      setWaitingError(extractApiError(err, 'The cost centres waiting for approval could not be loaded.'));
    }
  }, []);

  useEffect(() => { loadWaiting(); }, [loadWaiting]);

  // A bell links here as ?waiting=1#cc-<id>: once the list has loaded, light
  // that row up (the effect below scrolls to it).
  useEffect(() => {
    const match = /^#cc-(\d+)$/.exec(location.hash || '');
    if (!match || !waitingLoaded || focusedHashRef.current === location.hash) return;
    const id = parseInt(match[1], 10);
    const isWaiting = waiting.some(cc => cc.id === id);
    if (!isWaiting && refreshedForHashRef.current !== location.hash) {
      // Opened from a bell while this screen was already showing: the list may
      // be older than the cost centre. Ask once more before saying it is gone.
      refreshedForHashRef.current = location.hash;
      loadWaiting();
      return;
    }
    focusedHashRef.current = location.hash;
    if (!isWaiting) {
      showInfo('That cost centre is no longer waiting for approval.');
      return;
    }
    setHighlightId(id);
  }, [location.hash, waitingLoaded, waiting, showInfo, loadWaiting]);

  useEffect(() => {
    if (highlightId === null) return undefined;
    const scroll = setTimeout(() => {
      const row = document.getElementById(`cc-${highlightId}`);
      if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    const fade = setTimeout(() => setHighlightId(null), 6000);
    return () => { clearTimeout(scroll); clearTimeout(fade); };
  }, [highlightId]);

  const afterDecision = () => {
    loadWaiting();
    loadCentres(1, false);
  };

  const handleApproveOne = async (cc) => {
    setDecidingId(cc.id);
    try {
      if (cc.company_sends_to_abacus) {
        await approveCostCentre(cc.id, abacusCodes[cc.id] ?? cc.abacus_code ?? '');
      } else {
        await approveCostCentre(cc.id);
      }
      showSuccess(`Cost centre ${cc.cost_center_code} approved.`);
      afterDecision();
    } catch (err) {
      showError(extractApiError(err, `Cost centre ${cc.cost_center_code} could not be approved.`));
      loadWaiting();
    } finally {
      setDecidingId(null);
    }
  };

  const handleApproveSelected = async () => {
    if (selectedWaiting.length === 0) return;
    setApprovingSelected(true);
    try {
      const result = Object.keys(abacusCodes).length
        ? await approveCostCentres(selectedWaiting, abacusCodes)
        : await approveCostCentres(selectedWaiting);
      const skipped = (result.already_decided || 0) + (result.not_found || 0);
      const needCode = (result.need_abacus_code || []).length;
      const noCompany = result.no_company || 0;
      showSuccess(
        `${result.approved} cost centre${result.approved === 1 ? '' : 's'} approved.`
        + (skipped ? ` ${skipped} ${skipped === 1 ? 'was' : 'were'} no longer waiting.` : '')
        + (needCode ? ` ${needCode} still ${needCode === 1 ? 'needs its' : 'need their'} Abacus code.` : '')
        + (noCompany ? ` ${noCompany} still ${noCompany === 1 ? 'has' : 'have'} no company: set it with Edit first.` : '')
      );
      setSelectedWaiting([]);
      afterDecision();
    } catch (err) {
      showError(extractApiError(err, 'The ticked cost centres could not be approved.'));
      loadWaiting();
    } finally {
      setApprovingSelected(false);
    }
  };

  const startReject = (cc) => {
    setRejectingId(cc.id);
    setRejectReason('');
  };

  const handleConfirmReject = async (cc) => {
    setDecidingId(cc.id);
    try {
      await rejectCostCentre(cc.id, rejectReason.trim());
      showSuccess(`Cost centre ${cc.cost_center_code} rejected and switched off.`);
      setRejectingId(null);
      setRejectReason('');
      afterDecision();
    } catch (err) {
      showError(extractApiError(err, `Cost centre ${cc.cost_center_code} could not be rejected.`));
      loadWaiting();
    } finally {
      setDecidingId(null);
    }
  };

  const toggleWaitingTick = (id) => {
    setSelectedWaiting(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  // A cost centre with no company cannot be approved until it has one: the
  // server refuses it, so it is never ticked.
  const approvableWaiting = waiting.filter(cc => cc.entity);
  const allWaitingTicked = approvableWaiting.length > 0 && selectedWaiting.length === approvableWaiting.length;
  const toggleAllWaiting = () => {
    setSelectedWaiting(allWaitingTicked ? [] : approvableWaiting.map(cc => cc.id));
  };

  const loadPendingEntities = async () => {
    try {
      const data = await fetchPendingIMTEntities();
      setPendingEntities(data.pending_entities || []);
      setPendingCenters(data.pending_centers || 0);
    } catch {
      setPendingEntities([]);
      setPendingCenters(0);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const loadEntities = async () => {
    setEntitiesError(null);
    try {
      const data = await fetchHMSEntities();
      const list = data.results || data || [];
      setEntities(list);
      if (list.length === 1) setSelectedEntity(String(list[0].id));
    } catch (err) {
      console.error('Failed to load entities:', err);
      setEntitiesError(extractApiError(err, 'The list of companies could not be loaded, so no company can be chosen.'));
    }
  };

  const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const activeFiltersCount = Object.keys(activeFilters).length;

  const loadCentres = useCallback(async (page = 1, append = false) => {
    if (!selectedEntity) { setCentres([]); setTotalCount(0); setHasMore(false); return; }
    if (append) { setLoadingMore(true); } else { setLoading(true); }
    try {
      const data = await fetchCostCentersByEntity(selectedEntity, {
        page, search: debouncedSearch || undefined,
        filters: activeFiltersCount > 0 ? activeFilters : undefined,
      });
      const items = data.results || data || [];
      if (append) {
        setCentres(prev => [...prev, ...items]);
      } else {
        setCentres(items);
      }
      setTotalCount(data.count ?? items.length);
      setHasMore(!!data.next);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to load cost centres:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedEntity, debouncedSearch, activeFiltersCount]);

  useEffect(() => { loadCentres(1, false); }, [loadCentres]);

  const handleLoadMore = () => { loadCentres(currentPage + 1, true); };

  const openCreateModal = () => {
    setEditingCentre(null);
    setFormData({
      cost_center_code: '', cost_center_name: '', short_name: '',
      department: '', description: '', abacus_code: '', is_placeholder: false,
      entity: selectedEntity,
    });
    setShowCreateModal(true);
  };

  const openEditModal = (centre) => {
    setEditingCentre(centre);
    setFormData({
      cost_center_code: centre.cost_center_code,
      cost_center_name: centre.cost_center_name,
      short_name: centre.short_name || '',
      department: centre.department || '',
      description: centre.description || '',
      abacus_code: centre.abacus_code || '',
      is_placeholder: !!centre.is_placeholder,
      // The company is part of the form so the approver can set it on a cost
      // centre made for a delivery whose company was not recognised (IMT
      // divergence 07). For every other row it starts as the row's own company.
      entity: centre.entity ? String(centre.entity) : (centre.entity === null ? '' : selectedEntity),
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.cost_center_code || !formData.cost_center_name) {
      showError('Code and Name are required.');
      return;
    }
    // A company is required, except while editing a row that never had one
    // (the approver may fix its name before working out its company).
    if (!formData.entity && (!editingCentre || editingCentre.entity)) {
      showError('Choose the company this cost centre belongs to.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, entity: formData.entity ? parseInt(formData.entity, 10) : null };
      if (editingCentre) {
        await updateCostCenter(editingCentre.id, payload);
        showSuccess('Cost centre updated successfully.');
      } else {
        await createCostCenter(payload);
        showSuccess('Cost centre created successfully.');
      }
      setShowCreateModal(false);
      loadCentres(1, false);
      if (editingCentre && editingCentre.approval_status === 'pending') loadWaiting();
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        showError(Object.values(detail).flat().join(', '));
      } else {
        showError('Failed to save cost centre.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (centre) => {
    try {
      await updateCostCenter(centre.id, { is_active: !centre.is_active });
      loadCentres(1, false);
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  };

  const handleUpload = async (file, entityId) => await uploadCostCentresCSV(file, entityId);

  const handleUploadClose = () => {
    setShowUploadModal(false);
    loadCentres(1, false);
  };

  const openDeleteModal = (cc) => {
    setDeleteTarget(cc);
    setDeleteWarning(false);
    setDeleteWarningMsg('');
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteCostCenter(deleteTarget.id);
      showSuccess('Cost centre permanently deleted.');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadCentres(1, false);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.blockers) {
        const lines = data.blockers.map(b => b.type).join(', ');
        setDeleteWarningMsg(`${data.message}\n\nReferenced by: ${lines}`);
        setDeleteWarning(true);
      } else if (err.response?.status === 403) {
        setDeleteWarningMsg(data?.error || 'Permission denied.');
        setDeleteWarning(true);
      } else {
        showError(data?.error || 'Delete failed.');
        setShowDeleteModal(false);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Entity tabs for the shared page header. These pick the entity whose cost
  // centres are listed — same behaviour as the old strip, search included.
  const entityTabs = entities.map(e => ({
    key: String(e.id),
    label: e.hms_entity_name,
    icon: faBuilding,
  }));

  const selectEntity = (id) => {
    setSelectedEntity(id);
    setSearchTerm('');
    setDebouncedSearch('');
  };

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container master-data-screen cost-centres-screen">
        <HMSPageHeader
          onBack={() => history.push('/invoicing')}
          title="Cost Centres"
          kicker="Finance"
          tabs={entities.length > 0
            ? { items: entityTabs, activeKey: selectedEntity, onChange: selectEntity, alwaysLabel: true }
            : undefined}
          actions={
            <>
              <button className="btn-secondary" onClick={() => setShowUploadModal(true)} disabled={!selectedEntity}>
                <FontAwesomeIcon icon={faUpload} /> Upload CSV
              </button>
              <button className="btn-primary" onClick={openCreateModal} disabled={!selectedEntity}>
                <FontAwesomeIcon icon={faPlus} /> Create Cost Centre
              </button>
            </>
          }
        />

        {/* IMT-only: flags spec entities that have no corporate-structure record yet. */}
        {pendingEntities.length > 0 && !pendingBannerDismissed && (
          <div
            style={{
              background: '#fff8e1',
              border: '1px solid #f59e0b',
              borderLeft: '4px solid #f59e0b',
              borderRadius: 8,
              padding: '12px 16px',
              margin: '12px 0',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
            }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#b45309', marginTop: 4 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#78350f', marginBottom: 4 }}>
                {pendingEntities.length} {pendingEntities.length === 1 ? 'entity is' : 'entities are'} missing from the IMT master-data spec
                {pendingCenters > 0 && ` — ${pendingCenters} cost centre${pendingCenters === 1 ? '' : 's'} pending creation`}
              </div>
              <div style={{ fontSize: '0.9em', color: '#78350f', marginBottom: 8 }}>
                The IMT spec references these companies, but they don't exist in your corporate structure yet:
                <strong style={{ marginLeft: 6 }}>{pendingEntities.join(', ')}</strong>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  className="btn-primary"
                  style={{ fontSize: '0.85em', padding: '6px 12px' }}
                  onClick={() => history.push('/dashboard/corporate-structure')}
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> Create Entities
                </button>
                <button
                  style={{
                    fontSize: '0.85em', padding: '6px 12px', background: 'transparent',
                    border: '1px solid #b45309', color: '#78350f', borderRadius: 4, cursor: 'pointer',
                  }}
                  onClick={() => setPendingBannerDismissed(true)}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {waitingError && (
          <div style={{ padding: '0 2rem' }}>
            <ListLoadError message={waitingError} onRetry={loadWaiting} />
          </div>
        )}

        {/* IMT only (divergence 07): cost centres made for new deliveries,
            waiting for a Cost Centre Approver. Shown only while some wait. */}
        {waiting.length > 0 && (
          <div
            className="cost-centres-waiting"
            style={{
              background: 'white', border: `1px solid ${IMT_GOLD}`, borderLeft: `4px solid ${IMT_GOLD}`,
              borderRadius: 12, padding: '14px 16px', margin: '12px 0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <FontAwesomeIcon icon={faHourglassHalf} style={{ color: IMT_GREEN }} />
              <h3 style={{ margin: 0, color: IMT_GREEN_DARK, fontSize: '1.1em' }}>
                Waiting for approval ({waiting.length})
              </h3>
              {canApproveCostCentres && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: IMT_GREEN_DARK }}>
                    <input
                      type="checkbox"
                      checked={allWaitingTicked}
                      onChange={toggleAllWaiting}
                      aria-label="Tick every cost centre waiting for approval"
                    />
                    Tick all
                  </label>
                  <button
                    style={smallButton('approve')}
                    onClick={handleApproveSelected}
                    disabled={selectedWaiting.length === 0 || approvingSelected}
                  >
                    {approvingSelected
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Approving...</>
                      : <><FontAwesomeIcon icon={faCheck} /> Approve selected ({selectedWaiting.length})</>}
                  </button>
                </div>
              )}
            </div>
            <p style={{ margin: '0 0 10px', fontSize: '0.9em', color: '#555' }}>
              Made automatically when a new delivery was saved. They can already be used and show
              {' '}<strong>(not yet approved)</strong> in every cost-centre list until approved.
              {!canApproveCostCentres && ' Only people with the Cost Centre Approver role can approve or reject them.'}
            </p>

            {approversCount === 0 && (
              <div
                role="alert"
                style={{
                  background: '#fef2f2', border: '1px solid #b91c1c', borderLeft: '4px solid #b91c1c',
                  borderRadius: 8, padding: '10px 14px', marginBottom: 10, color: '#7f1d1d',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#b91c1c' }} />
                <span>
                  <strong>
                    {waiting.length} cost {waiting.length === 1 ? 'centre is' : 'centres are'} waiting and nobody is set to approve {waiting.length === 1 ? 'it' : 'them'}
                  </strong>
                  {' '}— give someone the Cost Centre Approver role (Admin → Assign Role).
                </span>
              </div>
            )}

            <table className="invoice-list-table">
              <thead>
                <tr>
                  {canApproveCostCentres && <th style={{ width: 36 }} aria-label="Tick" />}
                  <th>Code</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Delivery</th>
                  <th>Note</th>
                  <th>Abacus code</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {waiting.map(cc => (
                  <React.Fragment key={cc.id}>
                    <tr
                      id={`cc-${cc.id}`}
                      style={{
                        background: highlightId === cc.id ? IMT_GOLD_SOFT : undefined,
                        outline: highlightId === cc.id ? `2px solid ${IMT_GOLD}` : undefined,
                        transition: 'background 0.3s ease',
                      }}
                    >
                      {canApproveCostCentres && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedWaiting.includes(cc.id)}
                            onChange={() => toggleWaitingTick(cc.id)}
                            disabled={!cc.entity}
                            title={cc.entity ? undefined : 'Set its company with Edit first, then approve it.'}
                            aria-label={`Tick ${cc.cost_center_code}`}
                          />
                        </td>
                      )}
                      <td className="invoice-number-cell">
                        {cc.cost_center_code}
                        {!cc.is_active && <span style={pillStyle('#f3f4f6', '#4b5563')}>Switched off</span>}
                      </td>
                      <td>{cc.cost_center_name}</td>
                      <td>
                        {cc.entity_name || (
                          <span style={{ color: '#b91c1c', fontWeight: 600 }}>Not recognised — please set it</span>
                        )}
                      </td>
                      <td>{cc.source_delivery_number || '-'}</td>
                      <td style={{ maxWidth: 260, whiteSpace: 'pre-line', fontSize: '0.9em' }}>{cc.approval_note || '-'}</td>
                      <td>
                        {cc.company_sends_to_abacus && canApproveCostCentres ? (
                          <input
                            className="master-data-search-input"
                            style={{ width: 110 }}
                            value={abacusCodes[cc.id] ?? cc.abacus_code ?? ''}
                            onChange={(e) => setAbacusCodes((prev) => ({ ...prev, [cc.id]: e.target.value }))}
                            placeholder="Code in Abacus"
                            aria-label={`Abacus code for ${cc.cost_center_code}`}
                            title="This company sends its invoices to Abacus: add the cost centre in Abacus, then type its code here before approving."
                          />
                        ) : (cc.abacus_code || '-')}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{cc.created_at ? new Date(cc.created_at).toLocaleDateString() : '-'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button style={smallButton('edit')} onClick={() => openEditModal(cc)}>
                            <FontAwesomeIcon icon={faEdit} /> Edit
                          </button>
                          {canApproveCostCentres && (
                            <>
                              <button
                                style={smallButton('approve')}
                                onClick={() => handleApproveOne(cc)}
                                disabled={decidingId === cc.id || !cc.entity}
                                title={cc.entity ? undefined : 'Set its company with Edit first, then approve it.'}
                              >
                                <FontAwesomeIcon icon={decidingId === cc.id ? faSpinner : faCheck} spin={decidingId === cc.id} /> Approve
                              </button>
                              <button
                                style={smallButton('reject')}
                                onClick={() => startReject(cc)}
                                disabled={decidingId === cc.id || rejectingId === cc.id}
                              >
                                <FontAwesomeIcon icon={faBan} /> Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {canApproveCostCentres && rejectingId === cc.id && (
                      <tr>
                        <td colSpan={8} style={{ background: '#fafafa' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <label htmlFor={`reject-reason-${cc.id}`} style={{ fontWeight: 600, color: '#7f1d1d' }}>
                              Reject {cc.cost_center_code}? Reason (optional):
                            </label>
                            <input
                              id={`reject-reason-${cc.id}`}
                              className="md-form-input"
                              style={{ flex: 1, minWidth: 220 }}
                              type="text"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="Told to the person who created the delivery"
                              autoFocus
                            />
                            <button
                              style={smallButton('reject')}
                              onClick={() => handleConfirmReject(cc)}
                              disabled={decidingId === cc.id}
                            >
                              {decidingId === cc.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faBan} />} Reject and switch off
                            </button>
                            <button style={smallButton('edit')} onClick={() => { setRejectingId(null); setRejectReason(''); }}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {entitiesError && (
          <div style={{ padding: '0 2rem' }}>
            <ListLoadError message={entitiesError} onRetry={loadEntities} />
          </div>
        )}

        <div className="search-filter-controls">
          <div className="search-filter-wrapper">
            <div className="invoice-search-filter">
              <div className="search-header">
                <div className="search-input-group">
                  <div className="search-input-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      placeholder={selectedEntity ? "Search by code, name, department, or description..." : "Select an entity above..."}
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      disabled={!selectedEntity}
                      className="search-input compact"
                    />
                    {searchTerm && (
                      <button className="clear-search" onClick={() => setSearchTerm('')}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                  <button
                    className={`filter-toggle-button ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                    disabled={!selectedEntity}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    Filters
                    {activeFiltersCount > 0 && <span className="filter-count">{activeFiltersCount}</span>}
                    <FontAwesomeIcon icon={faChevronDown} className={`chevron ${showFilters ? 'rotated' : ''}`} />
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="search-filters-panel">
                  <div className="filters-grid">
                    <div className="form-group">
                      <label>Status</label>
                      <select value={filters.is_active} onChange={(e) => setFilters(f => ({ ...f, is_active: e.target.value }))} className="form-select">
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Department</label>
                      <select value={filters.department} onChange={(e) => setFilters(f => ({ ...f, department: e.target.value }))} className="form-select">
                        <option value="">All Departments</option>
                        {[...new Set(centres.map(c => c.department).filter(Boolean))].sort().map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="filters-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => { loadCentres(1, false); setShowFilters(false); }}>Apply</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setFilters({ is_active: '', department: '' }); setSearchTerm(''); }} disabled={activeFiltersCount === 0 && !searchTerm}>Clear All</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <button className="reload-button" onClick={() => loadCentres(1, false)} title="Reload cost centres" disabled={!selectedEntity}>
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>

        <div className="invoice-list-container">
          {!selectedEntity ? (
            /* `card` because the block this replaces drew its own white panel
               around the centred text. */
            <HMSEmptyState
              variant="card"
              icon={<FontAwesomeIcon icon={faBuilding} />}
              title="Select an Entity"
              hint="Choose an entity above to view its cost centres"
            />
          ) : loading ? (
            /* Skeleton in the shape of the cost-centre table that is coming.
               `.loading-state` keeps the padding the spinner block used; only
               its centring has to go, or the bars collapse to their content
               width. */
            <div className="loading-state" style={{ alignItems: 'stretch' }}>
              <HMSSkeleton shape="title" />
              <HMSSkeleton lines={6} />
            </div>
          ) : centres.length === 0 ? (
            <HMSEmptyState
              variant="card"
              icon={<FontAwesomeIcon icon={faBuilding} />}
              title={searchTerm ? 'No Results' : 'No Cost Centres'}
              hint={searchTerm ? 'No cost centres match your search' : 'No cost centres found for this entity'}
            />
          ) : (
            <>
            <table className="invoice-list-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Short</th>
                  <th>Abacus</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Linked Employee</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {centres.map(cc => (
                  <tr key={cc.id} style={cc.is_placeholder ? { color: '#9ca3af', fontStyle: 'italic' } : {}}>
                    <td className="invoice-number-cell">
                      {cc.cost_center_code}
                      {cc.is_placeholder && (
                        <span style={{
                          marginLeft: 6, fontSize: '0.7em', background: '#fef3c7', color: '#92400e',
                          padding: '2px 6px', borderRadius: 4, fontWeight: 500, fontStyle: 'normal',
                        }} title="Placeholder row from IMT spec — not yet defined">
                          PENDING
                        </span>
                      )}
                      {cc.approval_status === 'pending' && (
                        <span style={pillStyle(IMT_GOLD_SOFT, '#7a5a00')} title="Made for a new delivery; usable, but not yet approved">
                          Not yet approved
                        </span>
                      )}
                      {cc.approval_status === 'rejected' && (
                        <span
                          style={pillStyle('#fef2f2', '#b91c1c')}
                          title={cc.approval_note || 'Rejected by a Cost Centre Approver'}
                        >
                          Rejected
                        </span>
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>{cc.short_name || '-'}</td>
                    <td style={{ fontFamily: 'monospace', color: '#666' }}>{cc.abacus_code || '-'}</td>
                    <td>{cc.cost_center_name}</td>
                    <td>{cc.department || '-'}</td>
                    <td>{cc.linked_employee_name || '-'}</td>
                    <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cc.description || '-'}</td>
                    <td>
                      <HMSBadge status={cc.is_active ? 'approved' : 'draft'}>
                        {cc.is_active ? 'Active' : 'Inactive'}
                      </HMSBadge>
                    </td>
                    <td className="actions-cell">
                      <div
                        className={`action-dropdown ${activeDropdown === cc.id ? 'dropdown-open' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        ref={activeDropdown === cc.id ? dropdownRef : null}
                      >
                        <button
                          className="gear-button"
                          onClick={(e) => { e.stopPropagation(); toggleDropdown(cc.id); }}
                        >
                          Actions
                        </button>
                        {activeDropdown === cc.id && (
                          <div className="dropdown-menu">
                            <button onClick={() => { setActiveDropdown(null); openEditModal(cc); }}>
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            <hr />
                            <button onClick={() => { setActiveDropdown(null); handleToggleActive(cc); }}>
                              <FontAwesomeIcon icon={cc.is_active ? faToggleOff : faToggleOn} />
                              {cc.is_active ? ' Deactivate' : ' Activate'}
                            </button>
                            {isFinancialApprover && (
                              <>
                                <hr />
                                <button className="delete-action" onClick={() => openDeleteModal(cc)}>
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="master-data-load-more">
              <span className="load-more-count">
                Showing {centres.length.toLocaleString()} of {totalCount.toLocaleString()}
              </span>
              {hasMore && (
                <HMSButton variant="ghost" size="compact" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</> : 'Show 100 more'}
                </HMSButton>
              )}
            </div>
            </>
          )}
        </div>

        <Footer />
      </div>

      {showCreateModal && (
        <div className="md-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="md-modal" onClick={(e) => e.stopPropagation()}>
            <div className="md-modal-header">
              <div className="md-modal-header-text">
                <FontAwesomeIcon icon={editingCentre ? faEdit : faPlus} className="md-modal-header-icon" />
                <h2>{editingCentre ? 'Edit Cost Centre' : 'Create Cost Centre'}</h2>
              </div>
              <button className="md-modal-close" onClick={() => setShowCreateModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="md-modal-body">
              <div className="md-form-group">
                <label className="md-form-label">Company <span className="required">*</span></label>
                <select
                  className="md-form-input"
                  value={formData.entity || ''}
                  onChange={(e) => setFormData({ ...formData, entity: e.target.value })}
                >
                  <option value="">Choose a company…</option>
                  {entities.map(e => (
                    <option key={e.id} value={String(e.id)}>{e.hms_entity_name}</option>
                  ))}
                </select>
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Cost Centre Code <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.cost_center_code} onChange={(e) => setFormData({ ...formData, cost_center_code: e.target.value })} placeholder="e.g. 100, 2001" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Name <span className="required">*</span></label>
                <input className="md-form-input" type="text" value={formData.cost_center_name} onChange={(e) => setFormData({ ...formData, cost_center_name: e.target.value })} placeholder="e.g. Finance Department" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Shortname</label>
                <input className="md-form-input" type="text" value={formData.short_name || ''} onChange={(e) => setFormData({ ...formData, short_name: e.target.value })} placeholder="e.g. GB (for Gritt Bürger)" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Department</label>
                <input className="md-form-input" type="text" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} placeholder="e.g. Finance, IT" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Abacus Code</label>
                <input className="md-form-input" type="text" value={formData.abacus_code || ''} onChange={(e) => setFormData({ ...formData, abacus_code: e.target.value })} placeholder="e.g. 100, 101" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label">Description</label>
                <input className="md-form-input" type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Detailed description of cost centre purpose" />
              </div>
              <div className="md-form-group">
                <label className="md-form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!formData.is_placeholder}
                    onChange={(e) => setFormData({ ...formData, is_placeholder: e.target.checked })}
                  />
                  Placeholder (not yet defined)
                </label>
              </div>
            </div>
            <div className="md-modal-footer">
              <button className="md-btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="md-btn-submit" onClick={handleSave} disabled={saving}>
                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : editingCentre ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CSVUploadModal
        isOpen={showUploadModal} onClose={handleUploadClose} onUpload={handleUpload}
        title="Upload Cost Centres" templateColumns={TEMPLATE_COLUMNS}
        entityRequired={true} entities={entities} selectedEntity={selectedEntity}
      />

      <TradeOpsDeleteModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
        itemType="master_data"
        itemDetails={deleteTarget ? {
          recordType: 'Cost Centre',
          recordCode: deleteTarget.cost_center_code,
          recordName: deleteTarget.cost_center_name,
        } : {}}
        isWarning={deleteWarning}
        warningMessage={deleteWarningMsg}
        isDeleting={isDeleting}
      />
    </Layout>
  );
};

export default CostCentresPage;
